// lib/greeklishToGreek.ts
// Greeklish → Greek transliteration for search.
//
// "Greeklish" = Greek words typed with Latin characters, e.g. a user types
// "potiri" meaning "ποτήρι", or "kafetiera" meaning "καφετιέρα".
//
// --- Why this can be a simple character map ---
// Greeklish→Greek is genuinely ambiguous (a Latin "i" could be ι/η/υ/ει/οι).
// We do NOT try to produce orthographically correct Greek. The output of this
// function is fed straight through normalizeGreek(), which collapses every
// phonetically-equivalent vowel to one canonical form (η/υ/ει/οι → ι, ω → ο,
// αι → ε). So all VOWEL ambiguity disappears for free — we just pick any
// phonetic spelling and normalization lands it on the same token as the real
// Greek word.
//
// --- Known consonant ambiguities (best-guess conventions) ---
// normalizeGreek does NOT touch consonants, so these are deliberate choices:
//   b → μπ   (β is conventionally typed "v" in Greeklish)
//   v → β
//   x → χ    (ξ is conventionally typed "ks"; "maxairi" → μαχαίρι)
//   d → δ    (ντ is conventionally typed "nt"; "dh" digraph forces δ)
//   y → ι    (vowel use; γ-use like "yiayia" is rare)
//   h → χ    (standalone h)
// Words using the minority convention (e.g. "proboleas" for προβολέας) may
// miss — acceptable for v1; Meilisearch typo tolerance absorbs many near-misses.

// Digraphs — checked before single chars. Each 2-char key is unique, so lookup
// order does not matter.
const DIGRAPHS: Record<string, string> = {
  ps: 'ψ',
  th: 'θ',
  ch: 'χ',
  ks: 'ξ',
  gk: 'γκ',
  mp: 'μπ',
  nt: 'ντ',
  ts: 'τσ',
  tz: 'τζ',
  dh: 'δ',
  ou: 'ου',
  au: 'αυ',
  eu: 'ευ',
  ai: 'αι',
  ei: 'ει',
  oi: 'οι',
};

// Single Latin char → Greek. Anything not listed (digits, punctuation, already-
// Greek characters, whitespace) passes through unchanged.
const SINGLE: Record<string, string> = {
  a: 'α', b: 'μπ', c: 'κ', d: 'δ', e: 'ε', f: 'φ', g: 'γ',
  h: 'χ', i: 'ι', j: 'τζ', k: 'κ', l: 'λ', m: 'μ', n: 'ν',
  o: 'ο', p: 'π', q: 'κ', r: 'ρ', s: 'σ', t: 'τ', u: 'υ',
  v: 'β', w: 'ω', x: 'χ', y: 'ι', z: 'ζ',
};

/**
 * Transliterate Greeklish (Latin-typed Greek) into Greek script.
 *
 * The result is intentionally NOT guaranteed to be correct Greek spelling —
 * it is meant to be passed through normalizeGreek() before searching.
 *
 * Examples:
 *   greeklishToGreek("potiri")    → "ποτιρι"
 *   greeklishToGreek("kafetiera") → "καφετιερα"
 *   greeklishToGreek("maxairi")   → "μαχαιρι"
 *   greeklishToGreek("ποτήρι")    → "ποτήρι"   (Greek chars pass through)
 */
export function greeklishToGreek(text: string | null | undefined): string {
  if (!text) return '';

  const lower = text.toLowerCase();
  let out = '';
  let i = 0;

  while (i < lower.length) {
    const pair = lower.slice(i, i + 2);
    const digraph = DIGRAPHS[pair];
    if (digraph !== undefined) {
      out += digraph;
      i += 2;
      continue;
    }
    const ch = lower[i];
    out += SINGLE[ch] ?? ch;
    i += 1;
  }

  // Greek uses a distinct word-final sigma (ς). normalizeGreek()'s stemmer
  // matches suffixes written with ς (-ας, -ος, -ις, -ους, -ες), so a word-final
  // σ must become ς for stemming to line up with the indexed _normalized fields.
  return out.replace(/σ(?=\s|$)/g, 'ς');
}

/**
 * Heuristic: is this query worth transliterating?
 *
 * Returns false when the text already contains Greek (no transliteration
 * needed) or has no Latin letters at all (digits / punctuation only).
 */
export function looksLikeGreeklish(text: string | null | undefined): boolean {
  if (!text) return false;
  // Already Greek → leave it alone. Covers the Greek block (U+0370–U+03FF) and
  // Greek Extended (U+1F00–U+1FFF) — same coverage as containsGreek().
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 0;
    if ((code >= 0x0370 && code <= 0x03ff) || (code >= 0x1f00 && code <= 0x1fff)) {
      return false;
    }
  }
  // Needs at least one Latin letter to transliterate.
  return /[a-z]/i.test(text);
}
